import { toJS } from 'mobx';

import { MILLISECONDS_IN_SECOND } from '@/consts';
import { SecuredFile } from '../../../types/types';
import { UserInformation } from '../../UserStore/interfaces';
import { UploadedFile, UploadedItem } from '../../UploadFilesStore';
import { FilesForUpdate, UploadedFilesDict } from '../interfaces';

type UpdateAction = (fileId: string, isSilent?: boolean) => Promise<void>;

const isUploadedFile = (item: UploadedItem): item is UploadedFile => !item.isFolder && !!item.fid;

// Builds table rows straight from data already known when the upload finishes, so the file
// shows up immediately instead of waiting for the next list-files fetch to pick it up.
// `tryLoadNewFiles` still runs afterwards and its result overwrites these rows (matched by fid)
// with the authoritative data (real menu/permissions, corrected metadata, etc).
export const buildOptimisticFiles = (
    uploadedFiles: UploadedItem[],
    parentFolderId: string,
    userInfo: UserInformation,
): SecuredFile[] => {
    // creation_time/last_used_time are rendered via formatDate, which expects unix epoch seconds
    // (parseInt(dateStr, 10) * 1000), not an ISO string.
    const now = Math.floor(Date.now() / MILLISECONDS_IN_SECOND).toString();
    const owner = userInfo.displayName || userInfo.email;
    return uploadedFiles.filter(isUploadedFile).map((file) => {
        const filename = file.filename || file.name || '';
        return {
            fid: file.fid,
            file_id: file.fid,
            filename,
            filename_lowercase: filename.toLowerCase(),
            file_size: file.size ?? 0,
            current_state: 'secure',
            creation_time: now,
            last_used_time: now,
            last_used_by: owner,
            owner,
            owner_id: userInfo.userId,
            menu: undefined,
            policy: file.policy,
            policy_id: file.policy?.id || '',
            is_folder: false,
            parent_folder: parentFolderId,
            origin: '',
            origin_context: '',
            error_message: '',
            file_status: 'ready',
            policy_set_by_rule: false,
        };
    });
};

export const getFilesForUpdate = (
    currentList: SecuredFile[],
    uploadedFilesDict: UploadedFilesDict,
): FilesForUpdate => {
    const filesInList: SecuredFile[] = [];
    currentList.forEach((file) => {
        if (uploadedFilesDict[file.fid]) {
            filesInList.push(toJS(file));
        }
    });
    return {
        filesInList,
        filesOutOfListCount: Object.keys(uploadedFilesDict).length - filesInList.length,
    };
};

export const syncFetchedFilesWithUploaded = (
    fetchedFiles: SecuredFile[],
    uploadedFilesDict: UploadedFilesDict,
    updateFileAction: UpdateAction,
): SecuredFile[] => {
    const filesForUpdate: string[] = [];
    const newList: SecuredFile[] = fetchedFiles.map((file) => {
        const uploadedFile = uploadedFilesDict[file.fid];
        if (uploadedFile) {
            const { policy: uploadedFilePolicy } = uploadedFile;
            const { policy: fetchedFilePolicy } = file;
            if (uploadedFilePolicy && fetchedFilePolicy && (uploadedFilePolicy.id !== fetchedFilePolicy.id)) {
                filesForUpdate.push(file.fid);
                return { ...file, policy: { ...uploadedFilePolicy }, policy_id: uploadedFilePolicy.id };
            }
        }
        return file;
    });
    // file update calls is necessary to fetch actual file menu
    // TODO: probably it's not necessary because of frequent refresh
    Promise.all(filesForUpdate.map((fileId) => updateFileAction(fileId, true)));
    return newList;
};
